// =====================================================================
// Aba — Agenda por data (Instituto Melo / médicos viajantes).
// CRUD de clinic_availability_slots via /v2/admin/availability.
// Cada linha = "médico X atende na praça Y no dia D". Horário/vagas
// herdam o PADRÃO da unidade; só preenche start/end/capacity se o dia
// for diferente (ex.: "só de tarde"). Status 'closed' fecha o dia.
// Só aparece pra tenants com clinic_config.enable_date_agenda = true.
// =====================================================================
import React, { useState, useEffect, useMemo } from "react";
import { TextField, Chip, Box, Button } from "@material-ui/core";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";

import CrudTable from "../components/CrudTable";
import CrudDialog from "../components/CrudDialog";
import ConfirmDeleteDialog from "../components/ConfirmDeleteDialog";
import AgendaBulkImport from "../components/AgendaBulkImport";
import useCrudResource from "../hooks/useCrudResource";

import {
  listAvailability,
  createAvailability,
  updateAvailability,
  deleteAvailability,
  listUnits,
  listProfessionals
} from "../../../services/clinicAdminService";

const trimTime = (t) => (t ? String(t).slice(0, 5) : "");
const brDate = (d) =>
  d ? String(d).slice(0, 10).split("-").reverse().join("/") : "—";

// extrai o array de qualquer formato de resposta da Admin v2
const pickArray = (data) => {
  if (Array.isArray(data)) return data;
  if (data && typeof data === "object") {
    if (Array.isArray(data.rows)) return data.rows;
    if (Array.isArray(data.data)) return data.data;
    const k = Object.keys(data).find((key) => Array.isArray(data[key]));
    if (k) return data[k];
  }
  return [];
};

const timeField = (v, set) => (
  <TextField
    type="time"
    variant="outlined"
    size="small"
    fullWidth
    value={trimTime(v)}
    onChange={(e) => set(e.target.value)}
    InputLabelProps={{ shrink: true }}
    inputProps={{ step: 300 }}
  />
);

const AgendaPorDataTab = ({ slug }) => {
  const crud = useCrudResource({
    slug,
    list: listAvailability,
    create: createAvailability,
    update: updateAvailability,
    remove: deleteAvailability,
    resourceLabel: "dia de agenda"
  });

  const [units, setUnits] = useState([]);
  const [profs, setProfs] = useState([]);
  const [importOpen, setImportOpen] = useState(false);

  // carrega unidades + profissionais pra alimentar os selects do form
  useEffect(() => {
    if (!slug) return;
    let on = true;
    (async () => {
      try {
        const [u, p] = await Promise.all([
          listUnits(slug),
          listProfessionals(slug)
        ]);
        if (!on) return;
        setUnits(pickArray(u));
        setProfs(pickArray(p));
      } catch (e) {
        // selects ficam vazios; a lista principal já trata erro no hook
      }
    })();
    return () => {
      on = false;
    };
  }, [slug]);

  const unitName = useMemo(() => {
    const m = {};
    units.forEach((u) => {
      m[u.id] = u.name;
    });
    return m;
  }, [units]);

  const profName = useMemo(() => {
    const m = {};
    profs.forEach((p) => {
      m[p.id] = p.social_name || p.name;
    });
    return m;
  }, [profs]);

  const COLUMNS = useMemo(
    () => [
      { key: "available_date", label: "Data", width: 110, render: (r) => brDate(r.available_date) },
      {
        key: "unit_name",
        label: "Unidade",
        render: (r) => r.unit_name || unitName[r.unit_id] || "—"
      },
      {
        key: "professional_name",
        label: "Profissional",
        render: (r) => r.professional_name || profName[r.professional_id] || "—"
      },
      {
        key: "horario",
        label: "Horário",
        width: 120,
        render: (r) =>
          r.start_time && r.end_time
            ? `${trimTime(r.start_time)}–${trimTime(r.end_time)}`
            : "padrão"
      },
      {
        key: "capacity",
        label: "Vagas",
        width: 80,
        align: "right",
        render: (r) => (r.capacity != null ? r.capacity : "padrão")
      },
      {
        key: "status",
        label: "Status",
        width: 90,
        align: "center",
        render: (r) => (
          <Chip
            size="small"
            label={r.status === "closed" ? "fechado" : "aberto"}
            color={r.status === "closed" ? "default" : "primary"}
          />
        )
      }
    ],
    [unitName, profName]
  );

  const FIELDS = useMemo(
    () => [
      {
        key: "professional_id",
        label: "Profissional",
        type: "select",
        required: true,
        colSpan: 6,
        options: profs.map((p) => ({ value: p.id, label: p.social_name || p.name }))
      },
      {
        key: "unit_id",
        label: "Unidade (praça)",
        type: "select",
        required: true,
        colSpan: 6,
        options: units.map((u) => ({ value: u.id, label: u.name }))
      },
      {
        key: "available_date",
        label: "Data",
        type: "custom",
        required: true,
        colSpan: 6,
        render: (v, set) => (
          <TextField
            type="date"
            variant="outlined"
            size="small"
            fullWidth
            value={v ? String(v).slice(0, 10) : ""}
            onChange={(e) => set(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        )
      },
      {
        key: "status",
        label: "Status",
        type: "select",
        colSpan: 6,
        options: [
          { value: "open", label: "Aberto (atende)" },
          { value: "closed", label: "Fechado" }
        ],
        helperText: "Padrão: aberto"
      },
      {
        key: "start_time",
        label: "Abre às (só se diferente do padrão)",
        type: "custom",
        colSpan: 6,
        render: (v, set) => timeField(v, set)
      },
      {
        key: "end_time",
        label: "Fecha às (só se diferente)",
        type: "custom",
        colSpan: 6,
        render: (v, set) => timeField(v, set)
      },
      {
        key: "capacity",
        label: "Vagas (só se diferente do padrão)",
        type: "number",
        colSpan: 6,
        transform: (v) => (v === "" ? null : Number(v))
      },
      {
        key: "notes",
        label: "Observação",
        type: "text",
        colSpan: 6,
        placeholder: "ex.: só de tarde / curso à noite"
      }
    ],
    [units, profs]
  );

  return (
    <>
      <Box display="flex" justifyContent="flex-end" mb={1}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<CloudUploadIcon />}
          onClick={() => setImportOpen(true)}
        >
          Importar (colar)
        </Button>
      </Box>

      <CrudTable
        columns={COLUMNS}
        rows={crud.rows}
        loading={crud.loading}
        onAdd={crud.handleAdd}
        onEdit={crud.handleEdit}
        onDelete={crud.handleDelete}
        searchPlaceholder="Buscar por data / unidade…"
        searchFields={["available_date", "unit_name", "professional_name"]}
      />

      <AgendaBulkImport
        open={importOpen}
        onClose={() => setImportOpen(false)}
        slug={slug}
        units={units}
        profs={profs}
        onImported={crud.reload}
      />

      <CrudDialog
        open={crud.dialogOpen}
        onClose={crud.handleCloseDialog}
        onSave={crud.handleSave}
        title={crud.dialogInitial ? "Editar dia" : "Novo dia de agenda"}
        fields={FIELDS}
        initialValues={crud.dialogInitial}
        saving={crud.saving}
      />

      <ConfirmDeleteDialog
        open={!!crud.deleteTarget}
        onClose={crud.handleCancelDelete}
        onConfirm={crud.handleConfirmDelete}
        itemLabel={crud.deleteTarget && brDate(crud.deleteTarget.available_date)}
        saving={crud.saving}
      />
    </>
  );
};

export default AgendaPorDataTab;
